import { useEffect, useRef, useState } from "react";

import { getCurrentCountry, getKnowFrom } from "@config";
import { useCreateSsoSession } from "@modules/Authentication/hooks/useEmailLogin";
import {
  ICreateSsoSessionInput,
  TSsoSessionError,
  getSsoSessionError,
  isRecoverableSsoError,
} from "@modules/Authentication/services/ssoLogin";
import {
  ISsoCredential,
  ISsoProfile,
  TSsoProvider,
  fetchSsoProfile,
  isSsoCancelled,
  preloadSsoSdks,
  signInWithProvider,
} from "@modules/Authentication/services/ssoProviders";

import { AUTH_SCREEN_KEYS } from "../config/constants";
import messages from "../messages";
import { ISsoMissingInfoValues } from "../Components/SsoMissingInfoModal/SsoMissingInfoModal";
import { useAuthStepCompletion } from "./useAuthStepCompletion";

/** Fixed so a repeated failure replaces its toast instead of stacking. */
const SSO_FAILED_TOAST_ID = "taskCreation.auth.sso.failed";

export interface ISsoMissingInfoPrompt extends ISsoProfile {
  provider: TSsoProvider;
  /**
   * Whether the identity itself has no email, as opposed to the prefill
   * fetch below simply coming back empty — the only reliable signal, since
   * the endpoint checks for an email before it ever checks a name.
   */
  emailMissing: boolean;
}

/** Google/Facebook are verified from an access token; Apple from an id_token. */
const sessionCredential = (
  provider: TSsoProvider,
  credential: ISsoCredential
): Pick<ICreateSsoSessionInput, "token" | "os" | "id_token"> =>
  provider === "apple"
    ? { os: "web", id_token: credential.idToken }
    : { token: credential.token };

/**
 * Signing in through a provider, end to end: its popup, the session endpoint,
 * the missing-info form when it refuses for a name it was not given, and the
 * publish both auth branches finish with.
 */
export const useSsoSignIn = () => {
  const [pendingProvider, setPendingProvider] = useState<TSsoProvider>();
  const [missingInfo, setMissingInfo] = useState<ISsoMissingInfoPrompt>();

  /**
   * The credential the popup returned, kept for the retry the missing-info form
   * makes: reopening the popup to earn a second one would cost the visitor
   * another round with the provider.
   */
  const credential = useRef<ISsoCredential>();

  const { mutateAsync: createSession } = useCreateSsoSession();
  const {
    showAccountError,
    publishAndFinish,
    isPublishing,
    publishedRequestId,
  } = useAuthStepCompletion(SSO_FAILED_TOAST_ID, AUTH_SCREEN_KEYS.EMAIL);

  // The SDKs must open their popup from the click itself, and awaiting a
  // script inside the handler loses the user gesture the browser needs to see.
  useEffect(preloadSsoSdks, []);

  const reportFailure = () => {
    showAccountError(messages["taskCreation.error.saveFailed"]);
  };

  /**
   * Resolves to the refusal code when the endpoint named one, so the caller can
   * tell "ask for a name" from "this is over".
   */
  const submit = async (
    provider: TSsoProvider,
    cred: ISsoCredential,
    names?: ISsoMissingInfoValues
  ): Promise<TSsoSessionError | undefined> => {
    try {
      await createSession({
        provider,
        ...sessionCredential(provider, cred),
        country_code: getCurrentCountry(),
        know_from: getKnowFrom(),
        ...(names
          ? {
              firstname: names.firstName.trim(),
              lastname: names.lastName.trim(),
              // Omitted rather than sent empty: the endpoint rejects a `tel` it
              // cannot parse, and "no number" is not a number it can.
              ...(names.phoneNumber ? { phone_number: names.phoneNumber } : {}),
              // Only ever set when the provider withheld its own and the
              // visitor typed one in; otherwise the provider's wins server-side.
              ...(names.email ? { email: names.email.trim() } : {}),
            }
          : {}),
      });
    } catch (error) {
      return getSsoSessionError(error) ?? "registration_failed";
    }

    await publishAndFinish();

    return undefined;
  };

  const signIn = async (provider: TSsoProvider) => {
    setPendingProvider(provider);

    try {
      const cred = await signInWithProvider(provider);
      credential.current = cred;

      const code = await submit(provider, cred);
      if (!code) return;

      if (!isRecoverableSsoError(code)) {
        reportFailure();

        return;
      }

      // Apple hands the name to the popup (once, on first authorization); the
      // others expose it at userinfo, read here only to prefill the form.
      const profile =
        provider === "apple"
          ? cred.profile ?? {}
          : await fetchSsoProfile(provider, cred.token ?? "");
      setMissingInfo({
        provider,
        emailMissing: code === "no_email_received",
        ...profile,
      });
    } catch (error) {
      // Dismissing the provider's popup is a normal action, not a failure.
      if (!isSsoCancelled(error)) reportFailure();
    } finally {
      setPendingProvider(undefined);
    }
  };

  /**
   * Resolves to the refusal code when the retry is still short of something,
   * so the form can pin it to the field that owns it rather than toast it —
   * `SsoMissingInfoModal` is the one that knows which field that is.
   */
  const submitMissingInfo = async (
    values: ISsoMissingInfoValues
  ): Promise<TSsoSessionError | undefined> => {
    if (!missingInfo || !credential.current) return undefined;

    const code = await submit(missingInfo.provider, credential.current, values);
    if (!code) {
      setMissingInfo(undefined);

      return undefined;
    }

    if (!isRecoverableSsoError(code)) reportFailure();

    return code;
  };

  return {
    signIn,
    pendingProvider,
    missingInfo,
    submitMissingInfo,
    dismissMissingInfo: () => setMissingInfo(undefined),
    isPublishing,
    publishedRequestId,
  };
};
